import analytics from '../utils/analytics';
import { useEffect, useCallback } from 'react';
import { sentryCaptureException } from '../utils/sentry/sentry-client';
import { AdditionalAttributes } from '../utils/analytics/types';
import BrowserInteractionTime from 'browser-interaction-time';

/**
 * Length of time without any user interactions at which {@link BrowserInteractionTime} will suspend timing until next user interaction
 * @VisibleForTesting
 */
export const IDLE_TIMEOUT_MS = 30000;
/**
 * Initial interval at which to send a dwell time event. This interval changes based on the configured `multiplier` function.
 * @VisibleForTesting
 */
export const INITIAL_POLL_INTERVAL_MS = 5000;
/** Interval at which to check if a time-based callback should be fired */
const CHECK_CALLBACKS_INTERVAL_MS = 500;

/**
 * Calculates the next interval for dwell time analytics events
 * Used as input for {@link BrowserInteractionTime.addTimeIntervalEllapsedCallback}
 * @VisibleForTesting
 */
export const multiplier = (x: number) => x + x;

/**
 * Hook to track dwell time analytics
 *
 * Uses {@link BrowserInteractionTime} to keep track of cumulative time spent on a page,
 * and fire a "page dwelled" GASv3 event using callbacks on the following triggers:
 * - a dynamically increasing time interval (defined by `multiplier` function)
 * - a 'visibilitychange' event when `document.visibilityState !== 'visible'`
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilitychange_event
 * @see https://github.com/atlassian/browser-interaction-time/blob/7fec9483c6c1c2f174506131e380514ec7aa8fa0/src/browser-interaction-time.ts#L188
 *
 * Additionally, adds an event listener for `beforeunload` events that *should* send an event
 * with a `finalDwellEvent: true` property. This event is unreliable on mobile browsers.
 * This should be mostly mitigated by the usage of `visibilitychange` above.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
 * @see https://www.igvita.com/2015/11/20/dont-lose-user-and-app-state-use-page-visibility/
 *
 * | Trigger/Payload Attr | finalDwellEvent | visibilityChangeEvent |
 * |----------------------|-----------------|-----------------------|
 * | time interval        | false           | false                 |
 * | `visibilitychange`   | false           | true                  |
 * | `beforeunload`       | true            | false                 |
 *
 * Requires `eventContainer` as input, since it maps to `source` and is required by the spec:
 * @see https://hello.atlassian.net/wiki/spaces/AAPT/pages/134329336/UI+Events
 * `action` and `actionSubject` are provided by the `event` and `eventComponent` values in the hook itself.
 */
export const useDwellTimeAnalytics = (
    eventProps: { eventContainer: string } & AdditionalAttributes
) => {
    const trackDwellEvent = useCallback(
        (
            timeInMs: number,
            isFinalDwellEvent = false,
            isVisibilityChange = false
        ) => {
            try {
                analytics.trackEvent('page dwelled', {
                    ...eventProps,
                    dwellTime: timeInMs,
                    finalDwellEvent: isFinalDwellEvent,
                    visibilityChangeEvent: isVisibilityChange,
                    event: 'dwelled',
                    eventComponent: 'page'
                });
            } catch (error) {
                sentryCaptureException({ exception: error });
            }
        },
        [eventProps]
    );

    useEffect(() => {
        // No need to run on server-side
        if (typeof window === 'undefined') {
            return;
        }

        const browserInteractionTime = new BrowserInteractionTime({
            idleTimeoutMs: IDLE_TIMEOUT_MS,
            checkCallbacksIntervalMs: CHECK_CALLBACKS_INTERVAL_MS
        });
        browserInteractionTime.startTimer();

        browserInteractionTime.addTimeIntervalEllapsedCallback({
            callback: trackDwellEvent,
            timeInMilliseconds: INITIAL_POLL_INTERVAL_MS,
            multiplier
        });

        browserInteractionTime.addBrowserTabInactiveCallback((timeInMs) =>
            trackDwellEvent(timeInMs, false, true)
        );

        const handlePageUnload = () =>
            trackDwellEvent(
                browserInteractionTime.getTimeInMilliseconds(),
                true,
                false
            );

        window.addEventListener('beforeunload', handlePageUnload);

        return () => {
            browserInteractionTime.destroy();
            window.removeEventListener('beforeunload', handlePageUnload);
        };
    }, [trackDwellEvent]);

    return null;
};
